import {
  BUSINESS_DETAILS_ROUTE,
  COVER_DETAILS_BREAKDOWN_ROUTE,
  COVER_DETAILS_BUILDING_AND_CONTENTS_ROUTE,
  COVER_DETAILS_BUSINESS_INTERRUPTION_ROUTE,
  COVER_DETAILS_GLASS_ROUTE,
  COVER_DETAILS_LIABILITY_ROUTE,
  COVER_DETAILS_MONEY_ROUTE,
  COVER_DETAILS_PORTABLE_ITEMS_ROUTE,
  COVER_DETAILS_TAX_AUDIT_ROUTE,
  COVER_DETAILS_THEFT_ROUTE,
  EXTRA_INFO_CUSTOMER_HISTORY_ROUTE,
  EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE,
  MEMBERSHIP_ROUTE,
  PAYMENTS_ROUTE,
  QUOTE_ROUTE,
  REVIEW_ROUTE,
  SELECT_COVER_ROUTE,
} from '~/constants/routes';
import { Cover } from '~/types/cover';
import { Step } from '~/types/enums';
import { AppRoute } from '~/types/types';

export type StaticStep =
  | Step.SelectCover
  | Step.CoverDetailsLiability
  | Step.ExtraInfoCustomerHistory
  | Step.ExtraInfoMoreBusinessDetails
  | Step.Review
  | Step.Payments
  | Step.Confirmation
  | Step.Membership; // Add this line

export type DynamicStep =
  | Step.CoverDetailsBuildingAndContents
  | Step.CoverDetailsBusinessInterruption
  | Step.CoverDetailsPortableItems
  | Step.CoverDetailsTheft
  | Step.CoverDetailsMoney
  | Step.CoverDetailsGlass
  | Step.CoverDetailsBreakdown
  | Step.CoverDetailsTaxAudit
  | Step.Quote;

export const dynamicSteps = [
  Step.CoverDetailsBuildingAndContents,
  Step.CoverDetailsBusinessInterruption,
  Step.CoverDetailsPortableItems,
  Step.CoverDetailsTheft,
  Step.CoverDetailsMoney,
  Step.CoverDetailsGlass,
  Step.CoverDetailsBreakdown,
  Step.CoverDetailsTaxAudit,
  Step.Quote,
];

export type CoverStep = Exclude<DynamicStep, Step.Quote>;

export const steps: {
  step: Step;
  label: string;
  route: AppRoute;
}[] = [
  {
    step: Step.BusinessDetails,
    label: 'Business details',
    route: BUSINESS_DETAILS_ROUTE,
  },
  {
    step: Step.SelectCover,
    label: 'Choose cover',
    route: SELECT_COVER_ROUTE,
  },
  {
    step: Step.CoverDetailsLiability,
    label: 'Cover details',
    route: COVER_DETAILS_LIABILITY_ROUTE,
  },
  {
    step: Step.Membership, // Add this block
    label: 'Membership',
    route: MEMBERSHIP_ROUTE,
  },
  {
    step: Step.Quote,
    label: 'See quote',
    route: QUOTE_ROUTE,
  },
  {
    step: Step.ExtraInfoCustomerHistory,
    label: 'Extra info',
    route: EXTRA_INFO_CUSTOMER_HISTORY_ROUTE,
  },
  {
    step: Step.Review,
    label: 'Review and buy',
    route: REVIEW_ROUTE,
  },
];

export const stepToPreviousRoute: { [key in StaticStep]: AppRoute } = {
  [Step.SelectCover]: BUSINESS_DETAILS_ROUTE,
  [Step.CoverDetailsLiability]: SELECT_COVER_ROUTE,
  /** @note optional cover detail -> quote steps handled separately */
  [Step.Membership]: COVER_DETAILS_LIABILITY_ROUTE, // Add this line
  [Step.ExtraInfoCustomerHistory]: QUOTE_ROUTE,
  [Step.ExtraInfoMoreBusinessDetails]: EXTRA_INFO_CUSTOMER_HISTORY_ROUTE,
  [Step.Review]: EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE,
  [Step.Payments]: REVIEW_ROUTE,
  [Step.Confirmation]: PAYMENTS_ROUTE,
};

export const coverToRoute: { [key in Cover]: AppRoute } = {
  [Cover.Liability]: COVER_DETAILS_LIABILITY_ROUTE,
  [Cover.BuildingAndContents]: COVER_DETAILS_BUILDING_AND_CONTENTS_ROUTE,
  [Cover.BusinessInterruption]: COVER_DETAILS_BUSINESS_INTERRUPTION_ROUTE,
  [Cover.PortableItems]: COVER_DETAILS_PORTABLE_ITEMS_ROUTE,
  [Cover.Theft]: COVER_DETAILS_THEFT_ROUTE,
  [Cover.Money]: COVER_DETAILS_MONEY_ROUTE,
  [Cover.Glass]: COVER_DETAILS_GLASS_ROUTE,
  [Cover.Breakdown]: COVER_DETAILS_BREAKDOWN_ROUTE,
  [Cover.TaxAudit]: COVER_DETAILS_TAX_AUDIT_ROUTE,
};

export const coverDetailsStepToCover: { [key in CoverStep]: Cover } = {
  [Step.CoverDetailsBuildingAndContents]: Cover.BuildingAndContents,
  [Step.CoverDetailsBusinessInterruption]: Cover.BusinessInterruption,
  [Step.CoverDetailsPortableItems]: Cover.PortableItems,
  [Step.CoverDetailsTheft]: Cover.Theft,
  [Step.CoverDetailsMoney]: Cover.Money,
  [Step.CoverDetailsGlass]: Cover.Glass,
  [Step.CoverDetailsTaxAudit]: Cover.TaxAudit,
  [Step.CoverDetailsBreakdown]: Cover.Breakdown,
};

export const toStepCategory = {
  [Step.BusinessDetails]: 0,
  [Step.SelectCover]: 1,
  [Step.CoverDetailsLiability]: 2,
  [Step.CoverDetailsBuildingAndContents]: 2,
  [Step.CoverDetailsBusinessInterruption]: 2,
  [Step.CoverDetailsPortableItems]: 2,
  [Step.CoverDetailsTheft]: 2,
  [Step.CoverDetailsMoney]: 2,
  [Step.CoverDetailsGlass]: 2,
  [Step.CoverDetailsBreakdown]: 2,
  [Step.CoverDetailsTaxAudit]: 2,
  [Step.Membership]: 3,
  [Step.Quote]: 3,
  [Step.ExtraInfoCustomerHistory]: 4,
  [Step.ExtraInfoMoreBusinessDetails]: 4,
  [Step.Review]: 5,
  [Step.Payments]: 5,
  [Step.Confirmation]: 6,
};
